// interfaces
import { IClient } from "../interfaces/IClient";
import { QuoteSettingContainerType } from "../interfaces/settings/QuoteSettingContainerType";
import { ProductType } from "../interfaces/enums/ProductType";
import { ProductIntroType } from "../../compare/types/ProductIntroType";
import {
    getEmploymentTypeAsString,
    getPaymentPeriodAsString,
    getPremiumStructureAsString
} from "./enumHelpers";
import { QuoteSettingsType } from "../../compare/types/QuoteSettingType";
import { sortClientsForAdult } from "./clientHelpers";
import ISettingClientLife from "../interfaces/settings/clients/ISettingClientLife";
import ISettingClientTrauma from "../interfaces/settings/clients/ISettingClientTrauma";
import ISettingClientHealth from "../interfaces/settings/clients/ISettingClientHealth";
import ISettingClientMortgage from "../interfaces/settings/clients/ISettingClientMortgage";
import ISettingClientIncome from "../interfaces/settings/clients/ISettingClientIncome";
import { PaymentFrequency } from "../interfaces/enums/PaymentFrequency";

const productSettingsJoin = "; ";

function generateLifeStringForClient(settings?: ISettingClientLife): string {
    return !settings
        ? ""
        : `$${settings?.coverAmount.toLocaleString()} cover${productSettingsJoin}${getPremiumStructureAsString(
              settings?.premiumStructure
          )}`;
}

function generateTraumaStringForClient(settings?: ISettingClientTrauma): string {
    return !settings
        ? ""
        : `$${settings?.coverAmount.toLocaleString()} cover${productSettingsJoin}${settings?.traumaType.toLowerCase()}`;
}

function generateHealthStringForClient(settings?: ISettingClientHealth): string {
    const res = [];

    if (!settings) {
        return "";
    }

    res.push(`Excess $${settings?.excess}`);

    const options = [];

    if (settings.hasDentalAndOpticalOption) options.push("Dental & Optical");
    if (settings.hasGPOption) options.push("GP Option");
    if (settings.hasSpecialistsAndTestsOption) options.push("Specialists & Tests");

    if (options.length > 0) res.push(options.join(", "));

    return res.join(productSettingsJoin);
}

function generateIncomeStringForClient(settings?: ISettingClientIncome): string {
    const res = [];

    if (!settings) {
        return "";
    }

    res.push(
        `Occupation Class ${settings?.occupationClass} - ${getEmploymentTypeAsString(
            settings?.employmentType
        )}`
    );
    res.push(`$${settings?.monthlyCover.toLocaleString()} monthly`);
    res.push(`${settings?.waitPeriodWeeks} weeks wait`);
    res.push(`${getPaymentPeriodAsString(settings?.paymentPeriod).toLocaleLowerCase()}`);

    return res.join(productSettingsJoin);
}

function generateMortgageStringForClient(settings?: ISettingClientMortgage): string {
    const res = [];

    if (!settings) {
        return "";
    }

    res.push(
        `Occupation Class ${settings?.occupationClass} - ${getEmploymentTypeAsString(
            settings?.employmentType
        )}`
    );
    res.push(`$${settings?.monthlyCover.toLocaleString()} monthly`);
    res.push(`${settings?.waitPeriodWeeks} weeks wait`);
    res.push(`${getPaymentPeriodAsString(settings?.paymentPeriod).toLocaleLowerCase()}`);

    return res.join(productSettingsJoin);
}


export function getProductIntros(type?: ProductType): ProductIntroType {
    switch (type) {
        case ProductType.Life:
            return {
                productType: ProductType.Life,
                productTypeName: "life insurance",
                urlAbout: "/life-insurance/about",
                urlFAQ: "/life-insurance/life-insurance-faq"
            } as ProductIntroType;
        case ProductType.Health:
            return {
                productType: ProductType.Health,
                productTypeName: "health insurance",
                urlAbout: "/health-insurance/about",
                urlFAQ: "/health-insurance/health-insurance-faq"
            } as ProductIntroType;
        case ProductType.Trauma:
            return {
                productType: ProductType.Trauma,
                productTypeName: "trauma insurance",
                urlAbout: "/trauma-insurance/about",
                urlFAQ: "/trauma-insurance/trauma-insurance-faq"
            } as ProductIntroType;
        case ProductType.Income:
            return {
                productType: ProductType.Income,
                productTypeName: "income protection",
                urlAbout: "/income-protection/about",
                urlFAQ: "/income-protection/income-protection-faq"
            } as ProductIntroType;
        case ProductType.Mortgage:
            return {
                productType: ProductType.Mortgage,
                productTypeName: "mortgage insurance",
                urlAbout: "/mortgage-insurance/about",
                urlFAQ: "/mortgage-insurance/mortgage-insurance-faq"
            } as ProductIntroType;
    }
}

const getFrequencyMultiplier = (freq: PaymentFrequency): number => {
    switch (freq) {
        case PaymentFrequency.Weekly:
            return 52;
        case PaymentFrequency.Fortnightly:
            return 26;
        case PaymentFrequency.Monthly:
            return 12;
        case PaymentFrequency.Yearly:
            return 1;
        case PaymentFrequency.Unknown:
        default:
            return 1;
    }
};

const getConversionMultiplierBetweenFrequency = (
    startFreq: PaymentFrequency,
    target: PaymentFrequency
): number => {
    return getFrequencyMultiplier(startFreq) / getFrequencyMultiplier(target);
};

export const maxMonthlyIncomeCoverPercentage = 0.75;

const calculateMonthlyIncomeValue = (income: number): number => {
    if (!income || income <= 0) return 0;
    return (
        income *
        getConversionMultiplierBetweenFrequency(
            PaymentFrequency.Yearly,
            PaymentFrequency.Monthly
        )
    );
};

export const calculateMonthlyIncome = (income: number): number => {
    return Math.ceil(calculateMonthlyIncomeValue(income));
};

export const calculateMaxIncomeMonthlyCover = (income: number): number => {
    return Math.ceil(
        calculateMonthlyIncomeValue(income) * maxMonthlyIncomeCoverPercentage
    );
};

export const maxMonthlyMortgageCoverPercentage = 1.1;

const calculateMortgageMonthlyRepaymentsValue = (
    repaymentAmount: number,
    repaymentFrequency: PaymentFrequency
): number => {
    return (
        repaymentAmount *
        getConversionMultiplierBetweenFrequency(
            repaymentFrequency,
            PaymentFrequency.Monthly
        )
    );
};

export const calculateMortgageMonthlyRepayments = (
    repaymentAmount: number,
    repaymentFrequency: PaymentFrequency
): number => {
    return Math.floor(
        calculateMortgageMonthlyRepaymentsValue(repaymentAmount, repaymentFrequency)
    );
};

export const calculateMaxMortgageMonthlyRepayments = (
    repaymentAmount: number,
    repaymentFrequency: PaymentFrequency
): number => {
    return Math.floor(
        calculateMortgageMonthlyRepaymentsValue(repaymentAmount, repaymentFrequency) *
            maxMonthlyMortgageCoverPercentage
    );
};
